/**
 * 🗂️ Project Plan Service
 *
 * CRUD + metrics for project_plans.
 * Plans are the intermediate layer between Projects and assets (actions, issues, blueprints).
 *
 * Rules:
 *  - Always filter by organization_id
 *  - Never call Supabase directly in components
 */

import { supabase } from '../lib/supabaseClient';
import type { ProjectPlan, PlanType, PlanMetrics, ProjectPlansSummary, ProjectHealthStatus } from '../types/projectPlan';

export interface CreatePlanInput {
  project_id?: string | null;
  name: string;
  type: PlanType;
  description?: string;
  team_members?: string[];
}

export interface UpdatePlanInput {
  name?: string;
  description?: string;
  project_id?: string | null;
  team_members?: string[];
}

export class ProjectPlanService {
  static async listStandalonePlans(organizationId: string): Promise<ProjectPlan[]> {
    const { data, error } = await supabase
      .from('project_plans')
      .select('*')
      .eq('organization_id', organizationId)
      .is('project_id', null)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[ProjectPlanService.listStandalonePlans]', error.message);
      return [];
    }
    return (data ?? []) as ProjectPlan[];
  }

  /**
   * Get a single plan by ID.
   */
  static async getPlan(organizationId: string, planId: string): Promise<ProjectPlan | null> {
    const { data, error } = await supabase
      .from('project_plans')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', planId)
      .single();
    if (error) return null;
    return data as ProjectPlan;
  }

  /**
   * List all plans for a project.
   */
  static async listPlans(organizationId: string, projectId: string): Promise<ProjectPlan[]> {
    const { data, error } = await supabase
      .from('project_plans')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[ProjectPlanService.listPlans]', error.message);
      return [];
    }
    return (data ?? []) as ProjectPlan[];
  }

  /**
   * Create a new plan.
   */
  static async createPlan(organizationId: string, input: CreatePlanInput): Promise<ProjectPlan> {
    const { team_members, ...rest } = input;
    // Build insert payload — omit metadata if no team_members to avoid 400
    // on instances where the metadata column hasn't been migrated yet
    const payload: Record<string, unknown> = {
      organization_id: organizationId,
      project_id: rest.project_id ?? null,
      name: rest.name,
      type: rest.type,
      description: rest.description ?? null,
    };
    if (team_members && team_members.length > 0) {
      payload.metadata = { team_members };
    }
    const { data, error } = await supabase
      .from('project_plans')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    return data as ProjectPlan;
  }

  /**
   * Update a plan.
   */
  static async updatePlan(organizationId: string, planId: string, updates: UpdatePlanInput): Promise<ProjectPlan> {
    const { team_members, ...rest } = updates;
    const payload: Record<string, unknown> = {
      ...rest,
      updated_at: new Date().toISOString(),
    };
    if (team_members !== undefined) {
      payload.metadata = team_members.length > 0 ? { team_members } : null;
    }
    const { data, error } = await supabase
      .from('project_plans')
      .update(payload)
      .eq('organization_id', organizationId)
      .eq('id', planId)
      .select()
      .single();

    if (error) throw error;
    return data as ProjectPlan;
  }

  /**
   * Delete a plan. Assets (actions, issues, blueprints) keep plan_id = NULL via ON DELETE SET NULL.
   */
  static async deletePlan(organizationId: string, planId: string): Promise<void> {
    const { error } = await supabase
      .from('project_plans')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', planId);

    if (error) throw error;
  }

  /**
   * Get metrics for all plans in a project (from the view).
   */
  static async listPlanMetrics(organizationId: string, projectId: string): Promise<PlanMetrics[]> {
    const { data, error } = await supabase
      .from('project_plan_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId);

    if (error) {
      console.warn('[ProjectPlanService.listPlanMetrics]', error.message);
      return [];
    }
    return (data ?? []) as PlanMetrics[];
  }

  /**
   * Get metrics for a single plan.
   */
  static async getPlanMetrics(organizationId: string, planId: string): Promise<PlanMetrics | null> {
    const { data, error } = await supabase
      .from('project_plan_metrics')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('plan_id', planId)
      .single();

    if (error) return null;
    return data as PlanMetrics;
  }

  /**
   * Compute progress for a single plan based on its type and metrics.
   */
  static computePlanProgress(type: PlanType, m: PlanMetrics): number {
    switch (type) {
      case 'action':
        return m.actions_total > 0 ? Math.round((m.actions_completed / m.actions_total) * 100) : 0;
      case 'process':
        return m.executions_total > 0 ? Math.round((m.executions_completed / m.executions_total) * 100) : 0;
      case 'improvement':
        return m.issues_total > 0 ? Math.round((m.issues_resolved / m.issues_total) * 100) : 0;
      default:
        return 0;
    }
  }

  /**
   * Compute project health from aggregated metrics.
   */
  static computeProjectHealth(summary: ProjectPlansSummary): ProjectHealthStatus {
    if (summary.issues_critical > 0) return 'critical';
    if (summary.actions_overdue > 0 || summary.executions_sla_risk > 0) return 'warning';
    return 'healthy';
  }

  /**
   * Build a ProjectPlansSummary from an array of PlanMetrics.
   */
  static buildSummary(metrics: PlanMetrics[]): ProjectPlansSummary {
    const s: ProjectPlansSummary = {
      total_plans: metrics.length,
      actions_overdue: 0, actions_total: 0, actions_completed: 0,
      issues_critical: 0, issues_total: 0, issues_resolved: 0,
      executions_active: 0, executions_total: 0, executions_completed: 0,
      executions_sla_risk: 0, progress: 0,
    };
    let progressSum = 0;
    for (const m of metrics) {
      s.actions_overdue += m.actions_overdue ?? 0;
      s.actions_total += m.actions_total ?? 0;
      s.actions_completed += m.actions_completed ?? 0;
      s.issues_critical += m.issues_critical ?? 0;
      s.issues_total += m.issues_total ?? 0;
      s.issues_resolved += m.issues_resolved ?? 0;
      s.executions_active += m.executions_active ?? 0;
      s.executions_total += m.executions_total ?? 0;
      s.executions_completed += m.executions_completed ?? 0;
      s.executions_sla_risk += m.executions_sla_risk ?? 0;
      progressSum += ProjectPlanService.computePlanProgress(m.type, m);
    }
    s.progress = metrics.length > 0 ? Math.round(progressSum / metrics.length) : 0;
    return s;
  }

  /**
   * Get aggregated summary for a project (sum of all plan metrics).
   */
  static async getProjectSummary(organizationId: string, projectId: string): Promise<ProjectPlansSummary> {
    const metrics = await ProjectPlanService.listPlanMetrics(organizationId, projectId);
    return ProjectPlanService.buildSummary(metrics);
  }

  /**
   * Get summaries for all projects in org (for ProjectsPage cards).
   */
  static async listProjectSummaries(organizationId: string): Promise<Record<string, ProjectPlansSummary>> {
    const { data, error } = await supabase
      .from('project_plan_metrics')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) {
      console.warn('[ProjectPlanService.listProjectSummaries]', error.message);
      return {};
    }

    // Group by project
    const grouped: Record<string, PlanMetrics[]> = {};
    for (const m of (data ?? []) as PlanMetrics[]) {
      if (!grouped[m.project_id]) grouped[m.project_id] = [];
      grouped[m.project_id].push(m);
    }

    const result: Record<string, ProjectPlansSummary> = {};
    for (const [pid, metrics] of Object.entries(grouped)) {
      result[pid] = ProjectPlanService.buildSummary(metrics);
    }
    return result;
  }
}
